import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { FileUpload, ImagePreview } from '../../components/ui/file-upload';
import { 
  Plus, 
  Edit, 
  Trash2, 
  User,
  Mail,
  MapPin,
  Save,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadershipManagement = () => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    slug: '',
    image: '',
    short_bio: '',
    full_bio: [''],
    location: 'Perth, Australia',
    join_date: 'January 2025',
    email: '',
    linkedin: '#',
    expertise: '',
    achievements: ''
  });

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leadership/`, {
        withCredentials: true
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Failed to load leadership members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleBioChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      full_bio: prev.full_bio.map((paragraph, i) => i === index ? value : paragraph)
    }));
  };

  const addBioParagraph = () => {
    setFormData(prev => ({
      ...prev,
      full_bio: [...prev.full_bio, '']
    }));
  };

  const removeBioParagraph = (index) => {
    if (formData.full_bio.length > 1) {
      setFormData(prev => ({
        ...prev,
        full_bio: prev.full_bio.filter((_, i) => i !== index)
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      slug: '',
      image: '',
      short_bio: '',
      full_bio: [''],
      location: 'Perth, Australia',
      join_date: 'January 2025',
      email: '',
      linkedin: '#',
      expertise: '',
      achievements: ''
    });
    setEditingMember(null);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      title: member.title,
      slug: member.slug,
      image: member.image,
      short_bio: member.short_bio,
      full_bio: member.full_bio,
      location: member.location,
      join_date: member.join_date,
      email: member.email,
      linkedin: member.linkedin,
      expertise: member.expertise.join(', '),
      achievements: member.achievements.join('\n')
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      expertise: formData.expertise.split(',').map(item => item.trim()).filter(item => item),
      achievements: formData.achievements.split('\n').map(item => item.trim()).filter(item => item)
    };

    try {
      if (editingMember) {
        await axios.put(`${BACKEND_URL}/api/leadership/${editingMember.id}/`, submitData, {
          withCredentials: true
        });
        toast.success('Leadership member updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/leadership/`, submitData, {
          withCredentials: true
        });
        toast.success('Leadership member created successfully');
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Failed to save leadership member');
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this leadership member?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/leadership/${memberId}/`, {
        withCredentials: true
      });
      toast.success('Leadership member deleted successfully');
      loadMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Failed to delete leadership member');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-vincotek-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leadership members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-vincotek-dark">Leadership Team</h2>
          <p className="text-gray-600">Manage leadership profiles and information</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-vincotek-blue hover:bg-blue-600 text-white"
              onClick={() => resetForm()}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Member
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMember ? 'Edit Leadership Member' : 'Add New Leadership Member'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="slug">URL Slug</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    placeholder="auto-generated-from-name"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Profile Image</Label>
                  {formData.image ? (
                    <div className="space-y-3">
                      <ImagePreview 
                        imageUrl={formData.image}
                        onRemove={() => handleInputChange('image', '')}
                      />
                      <Input
                        value={formData.image}
                        onChange={(e) => handleInputChange('image', e.target.value)}
                        placeholder="Or enter image URL directly"
                        required
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <FileUpload 
                        onUpload={(uploadData) => handleInputChange('image', uploadData.file_url)}
                        accept="image/*"
                        maxSize={5}
                      />
                      <div className="text-center text-sm text-gray-500">
                        or
                      </div>
                      <Input
                        value={formData.image}
                        onChange={(e) => handleInputChange('image', e.target.value)}
                        placeholder="Enter image URL directly"
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="short_bio">Short Biography</Label>
                <Textarea
                  id="short_bio"
                  value={formData.short_bio}
                  onChange={(e) => handleInputChange('short_bio', e.target.value)}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Full Biography Paragraphs</Label>
                {formData.full_bio.map((paragraph, index) => (
                  <div key={index} className="flex space-x-2">
                    <Textarea
                      value={paragraph}
                      onChange={(e) => handleBioChange(index, e.target.value)}
                      placeholder={`Biography paragraph ${index + 1}`}
                      rows={4}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeBioParagraph(index)}
                      disabled={formData.full_bio.length === 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addBioParagraph}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Biography Paragraph
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="join_date">Join Date</Label>
                  <Input
                    id="join_date"
                    value={formData.join_date}
                    onChange={(e) => handleInputChange('join_date', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input
                    id="linkedin"
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expertise">Areas of Expertise (comma separated)</Label>
                <Textarea
                  id="expertise"
                  value={formData.expertise}
                  onChange={(e) => handleInputChange('expertise', e.target.value)}
                  placeholder="Strategic Leadership, Technology Innovation, Business Development"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="achievements">Key Achievements (one per line)</Label>
                <Textarea
                  id="achievements"
                  value={formData.achievements}
                  onChange={(e) => handleInputChange('achievements', e.target.value)}
                  placeholder="Achievement 1&#10;Achievement 2&#10;Achievement 3"
                  rows={4}
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <Button type="submit" className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  {editingMember ? 'Update Member' : 'Add Member'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map((member) => (
          <Card key={member.id} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  {member.image ? (
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-vincotek-dark">
                      {member.name}
                    </h3>
                    <p className="text-vincotek-blue font-medium text-sm">
                      {member.title}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(member)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(member.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                {member.short_bio}
              </p>
              
              <div className="space-y-2 text-xs text-gray-500">
                <div className="flex items-center">
                  <Mail className="h-3 w-3 mr-2" />
                  {member.email}
                </div>
                <div className="flex items-center">
                  <MapPin className="h-3 w-3 mr-2" />
                  {member.location}
                </div>
              </div>
              
              {member.expertise && member.expertise.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-600 mb-2">Expertise:</p>
                  <div className="flex flex-wrap gap-1">
                    {member.expertise.slice(0, 3).map((skill, index) => (
                      <span key={index} className="text-xs px-2 py-1 bg-vincotek-blue/10 text-vincotek-blue rounded">
                        {skill}
                      </span>
                    ))}
                    {member.expertise.length > 3 && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                        +{member.expertise.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {members.length === 0 && (
        <Card className="bg-white border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No leadership members found
            </h3>
            <p className="text-gray-500 mb-6">
              Add your first leadership team member to get started.
            </p>
            <Button 
              onClick={() => {
                resetForm();
                setIsDialogOpen(true);
              }}
              className="bg-vincotek-blue hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Member
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeadershipManagement;